import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { M2mGroup, M2mSnapshot } from '../../../services/m2mService';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { shorten } from './StrategiesUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface Props {
  formulaId: string;
  materialName: string;
  snapshot: M2mSnapshot | null;
  isLoading: boolean;
  colorsMap?: Record<string, string>;
  onCommoditySelect?: (_formulaCode: string) => void;
  threshold?: number;
}

// ─── Exported helpers (used by M2mDetail too) ───

export const M2M_MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export function generateMonthLabels(count: number): string[] {
  const now = new Date();
  const labels: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    labels.push(`${M2M_MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`);
  }
  return labels;
}

export function contractMonthToLabel(cm: string): string | null {
  const parts = cm.trim().split(/\s+/);
  if (parts.length !== 2) return null;
  const [monthStr, yearStr] = parts;
  const idx = M2M_MONTH_NAMES.indexOf(monthStr.toUpperCase().slice(0, 3));
  if (idx < 0) return null;
  return `${M2M_MONTH_NAMES[idx]} ${yearStr}`;
}

export function computeSensitivity(g: M2mGroup, shock: number, fxRate: number = 1): number {
  const lotSize = g.lotSize || 0;

  // For options: strike is the floor — don't count losses below strike, but upside always counts
  if (g.isOption && g.strikePrice != null) {
    const shockedPrice = g.closePrice * (1 + shock);
    const effectivePrice = Math.max(shockedPrice, g.strikePrice);
    const pnlChange = (effectivePrice - g.closePrice) * lotSize * g.lots;
    return (g.openPnl + pnlChange) * fxRate;
  }

  const rawPnl = g.openPnl + lotSize * g.lots * g.closePrice * shock;
  return rawPnl * fxRate;
}

/** Build currency→USD conversion rates from financial segments */
export function buildFxRates(snapshot: { financialSegments: Array<{ currency: string; conversionRateToUsd: number }> }): Record<string, number> {
  const rates: Record<string, number> = { USD: 1 };
  for (const seg of snapshot.financialSegments) {
    if (seg.currency && seg.conversionRateToUsd) {
      rates[seg.currency] = seg.conversionRateToUsd;
    }
  }
  return rates;
}

/** Actual worth of open positions = lots × lotSize × closePrice × fxRate */
export function computeActualWorth(groups: M2mGroup[], fxRates: Record<string, number>): number {
  return groups.reduce((sum, g) => {
    const rate = fxRates[g.currency] || 1;
    return sum + Math.abs(g.lots) * (g.lotSize || 0) * g.closePrice * rate;
  }, 0);
}

// ─── Formatting ───

const fmt = (n: number) => {
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const SHOCKS = [-0.10, -0.05, 0.05, 0.10] as const;

// ─── Row data used by both modes ───

interface RowData {
  key: string;
  label: string;
  lots: number;
  m2m: number;
  shocks: [number, number, number, number]; // -10%, -5%, +5%, +10%
}

function buildRows(groups: M2mGroup[], mode: 'commodity' | 'month', fxRates: Record<string, number> = {}): RowData[] {
  const map: Record<string, { label: string; lots: number; groups: M2mGroup[] }> = {};
  for (const g of groups) {
    const key = mode === 'commodity' ? (g.formulaCode || g.commodity) : g.contractMonth;
    const label = mode === 'commodity' ? g.commodity : g.contractMonth;
    if (!map[key]) map[key] = { label, lots: 0, groups: [] };
    map[key].lots += g.lots;
    map[key].groups.push(g);
  }
  return Object.entries(map)
    .sort((a, b) => mode === 'month'
      ? a[0].localeCompare(b[0])
      : Math.abs(b[1].groups.reduce((s, g) => s + g.openPnl * (fxRates[g.currency] || 1), 0)) - Math.abs(a[1].groups.reduce((s, g) => s + g.openPnl * (fxRates[g.currency] || 1), 0)))
    .map(([key, v]) => ({
      key,
      label: v.label,
      lots: v.lots,
      m2m: v.groups.reduce((s, g) => s + g.openPnl * (fxRates[g.currency] || 1), 0),
      shocks: SHOCKS.map(sh => v.groups.reduce((s, g) => s + computeSensitivity(g, sh, fxRates[g.currency] || 1), 0)) as [number, number, number, number],
    }));
}

function totalRow(rows: RowData[]): RowData {
  return {
    key: '__total__',
    label: 'Total',
    lots: rows.reduce((s, r) => s + r.lots, 0),
    m2m: rows.reduce((s, r) => s + r.m2m, 0),
    shocks: [0, 1, 2, 3].map(i => rows.reduce((s, r) => s + r.shocks[i], 0)) as [number, number, number, number],
  };
}

// ─── Tornado Chart ───

const TornadoChart: React.FC<{ rows: RowData[]; height?: number }> = ({ rows, height }) => {
  // For each row: downside bar (min→m2m, red), upside bar (m2m→max, green)
  const chartData = useMemo(() => ({
    labels: rows.map(r => r.label),
    datasets: [
      {
        label: 'Downside risk',
        data: rows.map(r => {
          const worst = Math.min(r.m2m, ...r.shocks);
          return [worst, r.m2m];
        }),
        backgroundColor: 'rgba(239, 68, 68, 0.30)',
        borderColor: 'rgba(239, 68, 68, 0.55)',
        borderWidth: 1,
        borderSkipped: false,
        barPercentage: 0.65,
        categoryPercentage: 0.8,
      },
      {
        label: 'Upside potential',
        data: rows.map(r => {
          const best = Math.max(r.m2m, ...r.shocks);
          return [r.m2m, best];
        }),
        backgroundColor: 'rgba(34, 197, 94, 0.30)',
        borderColor: 'rgba(34, 197, 94, 0.55)',
        borderWidth: 1,
        borderSkipped: false,
        barPercentage: 0.65,
        categoryPercentage: 0.8,
      },
    ],
  }), [rows]);

  // Plugin: zero line + per-row ±5% tick marks (read from chart data, no closure)
  const markerPlugin = {
    id: 'tornadoMarkers',
    afterDraw(chart: any) {
      const ctx = chart.ctx;
      const xScale = chart.scales.x;
      const yScale = chart.scales.y;
      if (!xScale || !yScale) return;
      const { top, bottom, left, right } = chart.chartArea;
      const dsDown = chart.data.datasets[0]?.data; // downside: [worst, m2m]
      const dsUp = chart.data.datasets[1]?.data;   // upside:   [m2m, best]
      if (!dsDown || !dsUp) return;

      ctx.save();

      // Bold zero line
      const zeroX = xScale.getPixelForValue(0);
      if (zeroX >= left && zeroX <= right) {
        ctx.beginPath();
        ctx.moveTo(zeroX, top);
        ctx.lineTo(zeroX, bottom);
        ctx.strokeStyle = '#374151';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      const numRows = chart.data.labels?.length || 0;
      const barHeight = numRows > 1
        ? Math.abs(yScale.getPixelForValue(1) - yScale.getPixelForValue(0)) * 0.3
        : 8;

      for (let i = 0; i < numRows; i++) {
        const yCenter = yScale.getPixelForValue(i);
        const down = dsDown[i]; // [worst, m2m]
        const up = dsUp[i];     // [m2m, best]
        if (!down || !up) continue;

        // -5% mark = midpoint of downside bar
        const mid5down = (down[0] + down[1]) / 2;
        const px5down = xScale.getPixelForValue(mid5down);
        if (px5down >= left && px5down <= right) {
          ctx.beginPath();
          ctx.moveTo(px5down, yCenter - barHeight);
          ctx.lineTo(px5down, yCenter + barHeight);
          ctx.strokeStyle = 'rgba(249, 115, 22, 0.70)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([3, 2]);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // +5% mark = midpoint of upside bar
        const mid5up = (up[0] + up[1]) / 2;
        const px5up = xScale.getPixelForValue(mid5up);
        if (px5up >= left && px5up <= right) {
          ctx.beginPath();
          ctx.moveTo(px5up, yCenter - barHeight);
          ctx.lineTo(px5up, yCenter + barHeight);
          ctx.strokeStyle = 'rgba(22, 163, 74, 0.70)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([3, 2]);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      ctx.restore();
    },
  };

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    animation: { duration: 300 },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: any[]) => items[0]?.label || '',
          label: (ctx: any) => {
            const [a, b] = ctx.raw as [number, number];
            return ` ${ctx.dataset.label}: ${fmt(a)} → ${fmt(b)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: (context: any) => context.tick?.value === 0 ? '#374151' : 'rgba(0,0,0,0.06)',
          lineWidth: (context: any) => context.tick?.value === 0 ? 1.5 : 1,
        },
        ticks: {
          font: { size: 10 },
          callback: (v: any) => {
            const n = Number(v);
            return `${n < 0 ? '-' : ''}$${shorten(Math.abs(n))}`;
          },
        },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 }, padding: 4 },
      },
    },
  }), []);

  const chartHeight = height || Math.max(80, rows.length * 40 + 40);

  return (
    <div>
      <div style={{ height: chartHeight }}>
        <Bar data={chartData as any} options={options as any} plugins={[markerPlugin]} />
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-1 text-[10px] text-gray-400">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.30)', border: '1px solid rgba(239, 68, 68, 0.55)' }} />
          Downside (–10%)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0 border-t-[1.5px] border-dashed" style={{ borderColor: 'rgba(249, 115, 22, 0.6)' }} />
          –5%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0 border-t-[1.5px] border-dashed" style={{ borderColor: 'rgba(22, 163, 74, 0.6)' }} />
          +5%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: 'rgba(34, 197, 94, 0.30)', border: '1px solid rgba(34, 197, 94, 0.55)' }} />
          Upside (+10%)
        </span>
      </div>
    </div>
  );
};

// ─── Sensitivity Table ───

const SensitivityTable: React.FC<{
  rows: RowData[];
  totals: RowData;
  onRowClick?: (_key: string) => void;
  clickable?: boolean;
}> = ({ rows, totals, onRowClick, clickable }) => {
  const cellColor = (v: number) => v < 0 ? 'text-red-500' : 'text-green-600';
  const m2mStyle = { backgroundColor: '#f9fafb' };
  const redStyle = { backgroundColor: '#fef2f2' };
  const greenStyle = { backgroundColor: '#f0fdf4' };

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-gray-400 border-b border-gray-200">
          <th className="text-left font-medium py-2 pr-2"></th>
          <th className="text-right font-medium py-2 px-2">Lots</th>
          <th className="text-right font-medium py-2 px-2 text-red-400" style={redStyle}>-10%</th>
          <th className="text-right font-medium py-2 px-2 text-orange-400" style={redStyle}>-5%</th>
          <th className="text-right font-bold py-2 px-3 text-gray-700" style={m2mStyle}>M2M</th>
          <th className="text-right font-medium py-2 px-2 text-emerald-500" style={greenStyle}>+5%</th>
          <th className="text-right font-medium py-2 px-2 text-green-600" style={greenStyle}>+10%</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr
            key={r.key}
            className={`border-b border-gray-50 ${clickable ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50/50'}`}
            onClick={() => clickable && onRowClick?.(r.key)}
          >
            <td className="py-2 pr-2 text-gray-700 font-medium">{r.label}</td>
            <td className="py-2 px-2 text-right tabular-nums text-gray-500">{r.lots}</td>
            <td className={`py-2 px-2 text-right tabular-nums ${cellColor(r.shocks[0])}`} style={redStyle}>{fmt(r.shocks[0])}</td>
            <td className={`py-2 px-2 text-right tabular-nums ${cellColor(r.shocks[1])}`} style={redStyle}>{fmt(r.shocks[1])}</td>
            <td className={`py-2 px-3 text-right tabular-nums font-bold ${cellColor(r.m2m)}`} style={m2mStyle}>{fmt(r.m2m)}</td>
            <td className={`py-2 px-2 text-right tabular-nums ${cellColor(r.shocks[2])}`} style={greenStyle}>{fmt(r.shocks[2])}</td>
            <td className={`py-2 px-2 text-right tabular-nums ${cellColor(r.shocks[3])}`} style={greenStyle}>{fmt(r.shocks[3])}</td>
          </tr>
        ))}
        <tr className="border-t-2 border-gray-200 font-semibold">
          <td className="py-2 pr-2 text-gray-800">Total</td>
          <td className="py-2 px-2 text-right tabular-nums text-gray-700">{totals.lots}</td>
          <td className={`py-2 px-2 text-right tabular-nums ${cellColor(totals.shocks[0])}`} style={redStyle}>{fmt(totals.shocks[0])}</td>
          <td className={`py-2 px-2 text-right tabular-nums ${cellColor(totals.shocks[1])}`} style={redStyle}>{fmt(totals.shocks[1])}</td>
          <td className={`py-2 px-3 text-right tabular-nums font-bold ${cellColor(totals.m2m)}`} style={m2mStyle}>{fmt(totals.m2m)}</td>
          <td className={`py-2 px-2 text-right tabular-nums ${cellColor(totals.shocks[2])}`} style={greenStyle}>{fmt(totals.shocks[2])}</td>
          <td className={`py-2 px-2 text-right tabular-nums ${cellColor(totals.shocks[3])}`} style={greenStyle}>{fmt(totals.shocks[3])}</td>
        </tr>
      </tbody>
    </table>
  );
};

// ─── Thin Health Bar (top panel — just 1 bar + 1 row of numbers) ───

interface HealthBarProps {
  snapshot: M2mSnapshot;
  threshold?: number;
  actualWorth?: number;
  isGeneral?: boolean;
  totals?: RowData;
}

const HealthBar: React.FC<HealthBarProps> = ({ snapshot, threshold, actualWorth, isGeneral = true, totals }) => {
  // ── Material mode: M2M P&L bar within sensitivity range ──
  if (!isGeneral && totals) {
    const m2m = totals.m2m;
    const worst = Math.min(m2m, ...totals.shocks);
    const best = Math.max(m2m, ...totals.shocks);
    const span = best - worst || 1;
    // Bar segments: downside (worst→0 or worst→m2m), current m2m marker, upside
    const downsidePct = Math.max(0, ((m2m - worst) / span) * 100);
    const upsidePct = Math.max(0, ((best - m2m) / span) * 100);
    // Zero line position within the range
    const zeroPct = worst < 0 && best > 0 ? ((0 - worst) / span) * 100 : null;
    return (
      <div className="px-3 py-1.5">
        <div className="relative h-3 rounded-full overflow-hidden flex" style={{ backgroundColor: '#e5e7eb' }}>
          <div className="h-full" style={{ width: `${downsidePct}%`, backgroundColor: m2m < 0 ? '#ef4444' : '#f59e0b' }} />
          <div className="h-full" style={{ width: `${upsidePct}%`, backgroundColor: '#22c55e' }} />
          {zeroPct != null && (
            <div className="absolute top-0 h-full w-0.5" style={{ left: `${zeroPct}%`, backgroundColor: '#374151', zIndex: 1 }} />
          )}
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
          <span>M2M: <span className={`font-bold ${m2m >= 0 ? 'text-green-600' : 'text-red-500'}`}>{fmt(m2m)}</span></span>
          <span>Range: <span className="text-red-400 font-medium">{fmt(worst)}</span> → <span className="text-green-500 font-medium">{fmt(best)}</span></span>
          {actualWorth ? (
            <span>Position value: <span className="text-blue-500 font-medium">{fmt(actualWorth)}</span></span>
          ) : null}
        </div>
      </div>
    );
  }

  // ── General mode: margin health bar + balance & position value (bigger on right) ──
  const { initialMargin, acctValueAtMarket, marginExcess, endingBalanceTotal } = snapshot.financialSummary;
  const posVal = actualWorth || 0;
  const balanceRef = endingBalanceTotal || 0;
  const barRef = Math.max(balanceRef, posVal) || 1;
  const marginPct = Math.min((initialMargin / barRef) * 100, 100);
  const excessPct = Math.min((Math.max(0, marginExcess) / barRef) * 100, 100);
  const thresholdPct = threshold ? Math.min(((initialMargin + threshold) / barRef) * 100, 100) : 0;
  const healthRatio = initialMargin > 0 ? acctValueAtMarket / initialMargin : 999;
  const healthLabel = healthRatio > 3 ? 'Healthy' : healthRatio > 1.5 ? 'Caution' : 'At Risk';
  const healthBg = healthRatio > 3 ? 'bg-green-50 text-green-700' : healthRatio > 1.5 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700';

  // Bigger value goes on the right
  const balanceLabel = <span>Balance: <span className="text-gray-600 font-medium">{fmt(endingBalanceTotal)}</span></span>;
  const posLabel = posVal ? <span>Position value: <span className="text-blue-500 font-medium">{fmt(posVal)}</span></span> : null;

  return (
    <div className="px-3 py-1.5">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="relative h-3 rounded-full overflow-hidden flex" style={{ backgroundColor: '#e5e7eb' }}>
            <div className="h-full" style={{ width: `${marginPct}%`, backgroundColor: '#ef4444' }} />
            <div className="h-full" style={{ width: `${excessPct}%`, backgroundColor: '#22c55e' }} />
            {threshold && thresholdPct > 0 && (
              <div className="absolute top-0 h-full w-0.5" style={{ left: `${thresholdPct}%`, backgroundColor: '#f59e0b', zIndex: 1 }} />
            )}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
            <span>Margin: <span className="text-red-500 font-medium">{fmt(initialMargin)}</span></span>
            <span>Excess: <span className={`font-medium ${marginExcess < 0 ? 'text-red-500' : 'text-green-600'}`}>{fmt(marginExcess)}</span></span>
            {posVal >= balanceRef ? <>{balanceLabel}{posLabel}</> : <>{posLabel}{balanceLabel}</>}
          </div>
        </div>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${healthBg}`}>{healthLabel}</span>
      </div>
    </div>
  );
};

// ─── Account Detail (right bottom panel) ───

export const M2mAccountDetail: React.FC<{ snapshot: M2mSnapshot | null; threshold?: number }> = ({ snapshot, threshold }) => {
  if (!snapshot) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4">
        <div className="text-gray-400 text-sm text-center py-8">No M2M data</div>
      </div>
    );
  }

  const {
    endingBalanceTotal, initialMargin,
    maintenanceMargin, marginExcess, futOpenTradeEquity, acctValueAtMarket,
  } = snapshot.financialSummary;

  // Build FX rates from financial segments for currency conversion
  const fxRates = buildFxRates(snapshot);

  // Actual worth of positions = lots × lotSize × closePrice (converted to USD)
  const actualWorth = computeActualWorth(snapshot.groups, fxRates);

  const healthRatio = initialMargin > 0 ? acctValueAtMarket / initialMargin : 999;
  const healthLabel = healthRatio > 3 ? 'Healthy' : healthRatio > 1.5 ? 'Caution' : 'At Risk';
  const healthBg = healthRatio > 3 ? 'bg-green-50 text-green-700 border-green-200'
    : healthRatio > 1.5 ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
    : 'bg-red-50 text-red-700 border-red-200';

  const balance = endingBalanceTotal || 1;
  const marginPct = Math.min((initialMargin / balance) * 100, 100);
  const excessPct = Math.min((Math.max(0, marginExcess) / balance) * 100, 100);
  const thresholdPct = threshold ? Math.min(((initialMargin + threshold) / balance) * 100, 100) : 0;

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-base font-semibold text-gray-900">Account Summary</span>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${healthBg}`}>{healthLabel}</span>
      </div>

      {/* Bar: full = Cash Balance. Red = Margin. Green = Excess. Gray = OTE losses. */}
      <div className="mb-4">
        <div className="text-xs font-medium text-gray-500 mb-1">Cash Balance</div>
        <div className="relative h-5 rounded-md overflow-hidden flex" style={{ backgroundColor: '#e5e7eb' }}>
          <div className="h-full" style={{ width: `${marginPct}%`, backgroundColor: '#ef4444' }} />
          <div className="h-full" style={{ width: `${excessPct}%`, backgroundColor: '#22c55e' }} />
          {threshold && thresholdPct > 0 && (
            <div className="absolute top-0 h-full w-0.5" style={{ left: `${thresholdPct}%`, backgroundColor: '#f59e0b', zIndex: 1 }} />
          )}
        </div>
        <div className="flex justify-between text-[10px] mt-0.5">
          <span className="text-red-400">Margin: {fmt(initialMargin)}</span>
          <span className="text-green-500">Excess: {fmt(marginExcess)}</span>
          <span className="text-gray-500">Balance: {fmt(endingBalanceTotal)}</span>
        </div>
      </div>

      {/* Waterfall breakdown */}
      <div className="divide-y divide-gray-100">
        <DetailRow color="#6366F1" label="Position Value" value={fmt(actualWorth)} bold />
        <div className="text-[10px] text-gray-400 pl-5 py-0.5">= Lots × Lot Size × Close Price</div>
        <div className="border-t border-gray-100" />
        <DetailRow color="#3B82F6" label="Cash Balance" value={fmt(endingBalanceTotal)} />
        <DetailRow color="#8B5CF6" label="Open Trade Equity" value={fmt(futOpenTradeEquity)} />
        <div className="border-t border-gray-200" />
        <DetailRow color="#0EA5E9" label="Account Value at Market" value={fmt(acctValueAtMarket)} bold />
        <div className="text-[10px] text-gray-400 pl-5 py-0.5">= Cash + Open Trade Equity</div>
        <DetailRow color="#EF4444" label="Initial Margin Required" value={fmt(initialMargin)} />
        {maintenanceMargin !== initialMargin && (
          <DetailRow color="#F97316" label="Maintenance Margin" value={fmt(maintenanceMargin)} />
        )}
        <div className="border-t border-gray-200" />
        <DetailRow
          color={marginExcess >= 0 ? '#22c55e' : '#EF4444'}
          label="Margin Excess"
          value={fmt(marginExcess)}
          bold
        />
        <div className="text-[10px] text-gray-400 pl-5 py-0.5">= Account Value − Initial Margin</div>
        {threshold && (
          <DetailRow color="#f59e0b" label="Alert Threshold" value={fmt(threshold)} />
        )}
      </div>

      {/* Segments */}
      {snapshot.financialSegments.length > 1 && (
        <div className="mb-3 mt-4 pt-3 border-t border-gray-200">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Currency Segments</div>
          <div className="space-y-2">
            {snapshot.financialSegments.map(seg => (
              <div key={seg.segmentLabel} className="bg-gray-50 rounded p-2">
                <div className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                  <span>{seg.segmentLabel}</span>
                  <span className="tabular-nums">{fmt(seg.acctValueAtMarket)}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-gray-500">
                  <span>Balance: <span className="text-gray-700 tabular-nums">{fmt(seg.endingBalance)}</span></span>
                  <span>Margin: <span className="text-gray-700 tabular-nums">{fmt(seg.initialMarginRequirement)}</span></span>
                  <span>Equity: <span className="text-gray-700 tabular-nums">{fmt(seg.totalEquity)}</span></span>
                  <span>Excess: <span className={`tabular-nums ${seg.marginExcess >= 0 ? 'text-green-600' : 'text-red-500'}`}>{fmt(seg.marginExcess)}</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-gray-400 pt-2 mt-auto border-t border-gray-100">
        Statement: {snapshot.statementDate}
      </div>
    </div>
  );
};

export const DetailRow: React.FC<{ color: string; label: string; value: string; bold?: boolean; sub?: boolean }> = ({ color, label, value, bold, sub }) => (
  <div className="flex items-center justify-between py-1.5">
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
      <span className={`text-sm ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</span>
    </div>
    <span className={`text-sm tabular-nums ${bold ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>{value}</span>
  </div>
);

// ─── Main Component ───

export const M2mManagement: React.FC<Props> = ({
  formulaId,
  materialName,
  snapshot,
  isLoading,
  onCommoditySelect,
  threshold,
}) => {
  const isGeneral = formulaId === GENERAL_ID || !formulaId;

  const fxRates = useMemo(() => snapshot ? buildFxRates(snapshot) : {}, [snapshot]);

  const rows = useMemo(() => {
    if (!snapshot) return [];
    const groups = isGeneral
      ? snapshot.groups
      : snapshot.groups.filter(g => g.formulaCode === formulaId);
    return buildRows(groups, isGeneral ? 'commodity' : 'month', fxRates);
  }, [snapshot, formulaId, isGeneral, fxRates]);

  const totals = useMemo(() => totalRow(rows), [rows]);

  const actualWorth = useMemo(() => {
    if (!snapshot) return 0;
    const groups = isGeneral
      ? snapshot.groups
      : snapshot.groups.filter(g => g.formulaCode === formulaId);
    return computeActualWorth(groups, fxRates);
  }, [snapshot, formulaId, isGeneral, fxRates]);

  if (isLoading) {
    return (
      <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
        <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading M2M data…</div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="flex flex-col bg-white shadow-md rounded-lg p-2 h-full">
        <div className="flex items-center justify-center h-full text-gray-400 text-sm">
          No M2M data available — upload and approve a broker statement PDF first
        </div>
      </div>
    );
  }

  const title = isGeneral ? 'Mark to Market' : `${materialName} — Mark to Market`;

  return (
    <div className="flex flex-col bg-white shadow-md rounded-lg h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-0">
        <div>
          <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
          <p className="text-[10px] text-gray-400">Statement: {snapshot.statementDate} · {snapshot.brokerAccount}</p>
        </div>
      </div>

      {/* Health bar (thin) */}
      <HealthBar snapshot={snapshot} threshold={threshold} actualWorth={actualWorth} isGeneral={isGeneral} totals={totals} />

      {/* Tornado chart — single total bar */}
      {rows.length > 0 && (
        <div className="px-3">
          <TornadoChart rows={[{ ...totals, label: isGeneral ? 'Total M2M' : materialName }]} height={80} />
        </div>
      )}

      {/* Sensitivity table */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 mt-3">
        <SensitivityTable
          rows={rows}
          totals={totals}
          onRowClick={isGeneral ? onCommoditySelect : undefined}
          clickable={isGeneral}
        />
      </div>
    </div>
  );
};
